import React, { useState, useRef, useEffect, useMemo } from 'react';
import { TreeNodeProps, TreeItem, CategoryItem } from '../types';
import { AddItemInput } from './AddItemInput';
import { CardDetail } from './CardDetail';
import { UpdateCardAI } from './UpdateCardAI';
import { AICardForm } from './AICardForm';
import { SHOW_PRACTICE_PHRASES_IN_MENU } from '../constants/uiVisibility';
import { PracticeCard } from './PracticeCard';
import { CardPracticePickerModal } from './CardPracticePickerModal';
import { PracticePickerModal } from './PracticePickerModal';
import { GeneratePhrasesModal } from './GeneratePhrasesModal';
import { GenerateCollocationsModal } from './GenerateCollocationsModal';
import { GenerateSynonymsModal } from './GenerateSynonymsModal';
import { GenerateAntonymsModal } from './GenerateAntonymsModal';
import { GenerateImageModal } from './GenerateImageModal';
import { FolderView } from './FolderView';
import { PracticePhrases } from './PracticePhrases';
import { MoveToModal } from './MoveToModal';
import { DropdownMenu, DropdownMenuItem, createEditAction, createDeleteAction } from './DropdownMenu';
import { FolderPracticeSession } from './FolderPracticeSession';
import { FolderPracticePhrasesSession } from './FolderPracticePhrasesSession';
import { CategoryPracticeSession } from './CategoryPracticeSession';
import { handleConditionalDelete } from '../utils/deleteUtils';
import { flattenTree } from '../utils/treeUtils';
import { apiClient } from '../services/api';
import { Card } from '../types/api';
import { AssignFolderModal } from './AssignFolderModal';
import { ContextualPracticeModal } from './ContextualPracticeModal';
import { GenerateStoryModal } from './GenerateStoryModal';
import { AddTextModal } from './AddTextModal';
import { AIFolderGenerator } from './AIFolderGenerator';
import { useInstance } from '../hooks/useInstance';
import { useAuth } from '../hooks/useAuth';
import { trackCardOpened, trackCardCreated, trackCardMoved } from '../utils/analytics';
import {
  ChevronRight,
  ChevronDown,
  Folder,
  FileText,
  Plus,
  GripVertical,
  Hash,
  Sparkles,
  Move,
  Zap,
} from 'lucide-react';

export const TreeNode: React.FC<TreeNodeProps> = ({
  item,
  onToggle,
  onRename,
  onDelete,
  onAddChild,
  onMove,
  onCategoryRefresh,
  level,
  categoryId,
  categories = [],
  isInstanceOwner: propIsInstanceOwner = false,
  isSelectionMode = false,
  isSelected = false,
  selectedCardIds,
  onSelectCard,
}) => {
  const { selectedInstanceId, instancesWithRoles } = useInstance();
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(item.name);
  const [showAddInput, setShowAddInput] = useState(false);
  const [addType, setAddType] = useState<'folder' | 'card'>('card');
  const [showDetail, setShowDetail] = useState(false);
  const [showDetailInEditMode, setShowDetailInEditMode] = useState(false);
  const [showUpdateAI, setShowUpdateAI] = useState(false);
  const [showAICardForm, setShowAICardForm] = useState(false);
  const [showPracticeCard, setShowPracticeCard] = useState(false);
  const [showCardPracticePicker, setShowCardPracticePicker] = useState(false);
  const [showCardContextualPractice, setShowCardContextualPractice] = useState(false);
  const [showGeneratePhrases, setShowGeneratePhrases] = useState(false);
  const [showCollocations, setShowCollocations] = useState(false);
  const [showSynonyms, setShowSynonyms] = useState(false);
  const [showAntonyms, setShowAntonyms] = useState(false);
  const [showGenerateImage, setShowGenerateImage] = useState(false);
  const [newCardId, setNewCardId] = useState<number | null>(null);
  const [showFolderView, setShowFolderView] = useState(false);
  const [showMoveToModal, setShowMoveToModal] = useState(false);
  const [showFolderPracticePicker, setShowFolderPracticePicker] = useState(false);
  const [showFolderContextualPractice, setShowFolderContextualPractice] = useState(false);
  const [showFolderGenerateStory, setShowFolderGenerateStory] = useState(false);
  const [showAddText, setShowAddText] = useState(false);
  const [showPracticeSession, setShowPracticeSession] = useState(false);
  const [showPracticePhrases, setShowPracticePhrases] = useState(false);
  const [showFolderPracticePhrases, setShowFolderPracticePhrases] = useState(false);
  const [showFolderWizardSession, setShowFolderWizardSession] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showAIFolderGenerator, setShowAIFolderGenerator] = useState(false);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [isDragTarget, setIsDragTarget] = useState(false);
  const nodeRef = useRef<HTMLDivElement>(null);
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const draggedItemRef = useRef<TreeItem | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const derivedIsInstanceOwner = useMemo(() => {
    if (!selectedInstanceId || !user) return false;
    const found = instancesWithRoles.find(i => i.instance.id === selectedInstanceId);
    return found?.user_role === 'owner' || found?.instance.created_by === user.id;
  }, [selectedInstanceId, instancesWithRoles, user]);
  const isInstanceOwner = propIsInstanceOwner || derivedIsInstanceOwner;

  const handleRename = () => {
    if (editName.trim() && editName.trim() !== item.name) {
      onRename(item.id, editName.trim());
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditName(item.name);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleRename();
    } else if (e.key === 'Escape') {
      handleCancelEdit();
    }
  };

  const handleAddChild = (name: string) => {
    onAddChild(item.id, addType, name);
    if (addType === 'card') trackCardCreated('manual');
    setShowAddInput(false);
  };

  const handleAddFolder = () => {
    setAddType('folder');
    setShowAddInput(true);
  };

  const handleAddCard = () => {
    setAddType('card');
    setShowAddInput(true);
  };

  const handleCardClick = () => {
    if (!isEditing) {
      setShowDetailInEditMode(false);
      setShowDetail(true);
      trackCardOpened(item.id);
    }
  };

  const handleFolderClick = () => {
    if (!isEditing && item.is_folder) {
      setShowFolderView(true);
    }
  };

  const handleDetailClose = () => {
    setShowDetail(false);
    setShowDetailInEditMode(false);
  };

  const handleDetailSave = () => {
    // Refresh the parent tree to show updated data
    window.location.reload();
  };

  const handleAICardSuccess = (cardId: number) => {
    setShowAICardForm(false);
    // Open the card detail view immediately to show the created card data
    setNewCardId(cardId);
  };

  const handleNewCardDetailClose = () => {
    setNewCardId(null);
    // Refresh the tree after closing to show the new card in the tree
    window.location.reload();
  };

  const handleNewCardDetailSave = () => {
    // Refresh the parent tree to show updated data
    window.location.reload();
  };

  // Check if target folder is a subfolder (has a parent_id)
  const isTargetSubfolder = (targetItem: TreeItem): boolean => {
    return targetItem.parent_id !== null && targetItem.parent_id !== undefined;
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent) => {
    if (isEditing) {
      e.preventDefault();
      return;
    }
    setIsDragging(true);
    e.dataTransfer.effectAllowed = 'move';
    const dragData = {
      id: item.id,
      type: item.type,
      parent_id: item.parent_id,
      category_id: item.category_id,
      is_folder: item.is_folder,
      name: item.name,
    };
    // Set data in multiple formats for better browser compatibility
    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
    e.dataTransfer.setData('text/plain', item.id.toString()); // Fallback
    draggedItemRef.current = item;
    // Add visual feedback
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    setIsDragging(false);
    setDragOver(false);
    // Don't clear draggedItemRef here - it's needed in handleDrop
    // It will be cleared after the drop is processed
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only folders can be drop targets
    if (!item.is_folder) {
      e.dataTransfer.dropEffect = 'none';
      setDragOver(false);
      setIsDragTarget(false);
      return;
    }
    
    // Check if we have a dragged item
    if (!draggedItemRef.current) {
      // Try to get it from dataTransfer as fallback
      try {
        const dragDataStr = e.dataTransfer.getData('application/json');
        if (dragDataStr) {
          const dragData = JSON.parse(dragDataStr);
          // We can't fully validate without the full item, but allow the drop
          e.dataTransfer.dropEffect = 'move';
          setDragOver(true);
          setIsDragTarget(true);
          return;
        }
      } catch (err) {
        // Ignore parse errors
      }
      e.dataTransfer.dropEffect = 'none';
      setDragOver(false);
      setIsDragTarget(false);
      return;
    }
    
    // Validate if dragged item can be moved to this folder
    if (canMoveToTarget(draggedItemRef.current, item)) {
      e.dataTransfer.dropEffect = 'move';
      setDragOver(true);
      setIsDragTarget(true);
    } else {
      e.dataTransfer.dropEffect = 'none';
      setDragOver(false);
      setIsDragTarget(false);
    }
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    setIsDragTarget(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('DROP EVENT FIRED on:', item.name, 'is_folder:', item.is_folder);
    setDragOver(false);
    setIsDragTarget(false);
    
    try {
      // Get drag data from dataTransfer (this works in drop event)
      let dragData: any = null;
      let draggedItemId: number | null = null;
      
      try {
        const dragDataStr = e.dataTransfer.getData('application/json');
        console.log('Drag data string:', dragDataStr);
        if (dragDataStr) {
          dragData = JSON.parse(dragDataStr);
          draggedItemId = dragData.id;
          console.log('Parsed drag data:', dragData);
        }
      } catch (parseErr) {
        console.warn('Could not parse JSON drag data, trying text/plain:', parseErr);
        // Fallback to text/plain
        const textData = e.dataTransfer.getData('text/plain');
        console.log('Text/plain data:', textData);
        if (textData) {
          draggedItemId = parseInt(textData, 10);
        }
      }
      
      // Try to get item from ref first (works if dragging within same component)
      let draggedItem = draggedItemRef.current;
      console.log('Dragged item from ref:', draggedItem);
      
      // If ref is null, try to construct minimal item from drag data
      if (!draggedItem && dragData) {
        draggedItem = {
          id: dragData.id,
          name: dragData.name || 'Unknown',
          type: dragData.type || 'card',
          parent_id: dragData.parent_id,
          is_folder: dragData.is_folder || false,
          children: [],
          category_id: dragData.category_id,
        } as TreeItem;
        console.log('Constructed dragged item from drag data:', draggedItem);
      }
      
      // If we still don't have an item ID, we can't proceed
      if (!draggedItemId) {
        console.error('Could not determine dragged item ID. Drag data:', dragData);
        alert('Failed to move: Could not identify dragged item');
        return;
      }
      
      // Ensure we're dropping on a folder
      if (!item.is_folder) {
        console.log('Drop target is not a folder, ignoring drop');
        return;
      }
      
      // Can't move item to itself
      if (draggedItemId === item.id) {
        console.log('Cannot move item to itself');
        return;
      }
      
      // Validate move if we have the full item
      if (draggedItem && !canMoveToTarget(draggedItem, item)) {
        alert('Cannot move here. Subfolders cannot contain other folders.');
        return;
      }

      console.log('Moving item:', {
        itemId: draggedItemId,
        itemName: draggedItem?.name || 'Unknown',
        targetFolderId: item.id,
        targetFolderName: item.name
      });

      // Move to this folder - use the ID we got from drag data
      await onMove(draggedItemId, { parent_id: item.id });
      trackCardMoved(draggedItemId);
      
      // Clear the ref after successful move
      draggedItemRef.current = null;
    } catch (err: any) {
      console.error('Error handling drop:', err);
      draggedItemRef.current = null;
      alert(err.response?.data?.detail || err.message || 'Failed to move item');
    }
  };

  // Check if dragged item can be moved to target
  const canMoveToTarget = (draggedItem: TreeItem, targetItem: TreeItem): boolean => {
    // Can't move to itself
    if (draggedItem.id === targetItem.id) return false;
    
    // Can't move to its own children (prevent circular reference)
    const isDescendant = (parent: TreeItem, childId: number): boolean => {
      if (parent.id === childId) return true;
      return parent.children.some((child) => isDescendant(child, childId));
    };
    if (isDescendant(draggedItem, targetItem.id)) return false;
    
    // Folders can't be moved into subfolders
    if (draggedItem.is_folder && targetItem.is_folder && isTargetSubfolder(targetItem)) {
      return false;
    }
    
    return true;
  };

  // Mobile long-press handler
  const handleTouchStart = (e: React.TouchEvent) => {
    if (isEditing || !isMobile) return;
    
    longPressTimer.current = setTimeout(() => {
      setIsDragging(true);
      // Trigger drag on mobile
      const touch = e.touches[0];
      const dragEvent = new DragEvent('dragstart', {
        bubbles: true,
        cancelable: true,
      });
      // Note: Touch drag is limited, so we'll use Move To modal instead
    }, 500); // 500ms long press
  };

  const handleTouchEnd = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
    setIsDragging(false);
  };

  const handleTouchMove = () => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current);
      longPressTimer.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (longPressTimer.current) {
        clearTimeout(longPressTimer.current);
      }
    };
  }, []);

  // Create dropdown menu items - memoized to update when isInstanceOwner changes
  const dropdownItems = useMemo((): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [];

    // For cards (is_folder = false)
    if (!item.is_folder) {
      if (SHOW_PRACTICE_PHRASES_IN_MENU) {
        items.push({
          id: 'practice-phrases',
          label: 'Practice Phrases',
          icon: <Sparkles className="h-4 w-4" />,
          onClick: () => {
            setShowPracticePhrases(true);
          },
        });
      }
      
      if (isInstanceOwner) {
        items.push(
          {
            id: 'move',
            label: 'Move To...',
            icon: <Move className="h-4 w-4" />,
            onClick: () => {
              setShowMoveToModal(true);
            },
          },
          // For cards, Edit opens CardDetail in edit mode instead of inline rename
          createEditAction(() => {
            if (!item.is_folder) {
              setShowDetailInEditMode(true);
              setShowDetail(true);
            } else {
              setIsEditing(true);
            }
          }),
          createDeleteAction(() => handleConditionalDelete(item, () => onDelete(item.id)))
        );
      }
    }

    // For folders (is_folder = true)
    if (item.is_folder) {
      if (SHOW_PRACTICE_PHRASES_IN_MENU) {
        items.push({
          id: 'practice-phrases',
          label: 'Practice Phrases',
          icon: <Sparkles className="h-4 w-4" />,
          onClick: () => setShowFolderPracticePhrases(true),
        });
      }

      // Only show Move To and Assign if user is instance owner
      if (isInstanceOwner) {
        items.push(
          {
            id: 'move',
            label: 'Move To...',
            icon: <Move className="h-4 w-4" />,
            onClick: () => {
              console.log('Move action clicked for item:', item.name, 'parent_id:', item.parent_id, 'categories:', categories.length);
              setShowMoveToModal(true);
            },
          },
          createEditAction(() => setIsEditing(true)),
          createDeleteAction(() => handleConditionalDelete(item, () => onDelete(item.id)))
        );
      }
    }

    return items;
  }, [item, isInstanceOwner, onDelete, categories]);

  // Handle AI update success
  const handleAIUpdateSuccess = async (updatedCard: Card) => {
    setShowUpdateAI(false);
    // Open the card detail view with updated data
    // The CardDetail component will automatically reload the card data
    setShowDetail(true);
  };

  // Calculate indentation based on level
  const indentPx = level * 24; // Increased from 20px for better visual separation
  
  // Get item count for folders
  const itemCount = item.children ? item.children.length : 0;
  const hasChildren = item.children && item.children.length > 0;
  
  // Different styling based on level and type
  const isTopLevelFolder = level === 0 && item.is_folder;
  const isSubfolder = level === 1 && item.is_folder;
  const isCard = !item.is_folder;

  return (
    <div className="relative">
      {/* Main item container */}
      <div 
        ref={nodeRef}
        draggable={!isEditing && !isMobile}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onTouchStart={isMobile ? handleTouchStart : undefined}
        onTouchEnd={isMobile ? handleTouchEnd : undefined}
        onTouchMove={isMobile ? handleTouchMove : undefined}
        className={`group transition-all duration-200 ${
          isTopLevelFolder 
            ? 'py-3 px-4 border-l-2 border-gray-300 rounded-lg mb-2 hover:bg-gray-50'
            : isSubfolder
            ? 'py-2 px-3 ml-6 bg-gray-50 border-l-2 border-gray-300 rounded-md mb-1 hover:bg-gray-100'
            : isCard
            ? 'py-1.5 px-3 ml-8 hover:bg-gray-50 rounded-md border-l border-gray-200'
            : 'py-2 px-3 hover:bg-gray-50 rounded-md'
        } ${
          isDragging ? 'opacity-50 cursor-grabbing' : 'cursor-move'
        } ${
          dragOver && item.is_folder ? 'bg-green-100 border-green-400 border-2 ring-2 ring-green-300' : ''
        }`}
        style={{ marginLeft: `${indentPx}px` }}
      >
        {/* Desktop layout - single row */}
        <div
          className="hidden lg:flex items-center flex-1 min-w-0"
          onDragOver={(e) => {
            // Allow drop on nested elements
            if (item.is_folder) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onDrop={(e) => {
            // Forward drop to parent handler
            if (item.is_folder) {
              handleDrop(e);
            }
          }}
        >
          {/* Selection checkbox - only for cards in selection mode */}
          {isSelectionMode && isCard && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                onSelectCard?.(item.id, e.target.checked);
              }}
              onClick={(e) => e.stopPropagation()}
              className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer flex-shrink-0"
            />
          )}

          {/* Drag handle - desktop only */}
          <div
            className="mr-2 transition-opacity opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <GripVertical className="h-3 w-3 text-gray-400" />
          </div>

          {/* Expand/collapse button - only for items with children */}
          {hasChildren && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggle(item.id);
              }}
              onMouseDown={(e) => e.stopPropagation()}
              className={`mr-2 p-1 hover:bg-gray-200 rounded transition-colors ${
                isTopLevelFolder ? 'hover:bg-blue-200' : ''
              }`}
            >
              {item.isExpanded ? (
                <ChevronDown className="h-4 w-4 text-gray-600" />
              ) : (
                <ChevronRight className="h-4 w-4 text-gray-600" />
              )}
            </button>
          )}

          {/* Spacer for items without expand button */}
          {!hasChildren && <div className="w-6 mr-2" />}

          {/* Icon with different styling based on type and level */}
          <div className="mr-3 flex-shrink-0">
            {(item.is_folder === true || item.type === 'folder') ? (
              <Folder className={`${isTopLevelFolder ? 'h-5 w-5 text-blue-600' : 'h-4 w-4 text-blue-500'}`} />
            ) : (
              <FileText className="h-3.5 w-3.5 text-gray-500" />
            )}
          </div>

          {/* Name with appropriate styling */}
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {isEditing ? (
              <input
                type="text"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                onBlur={handleRename}
                onKeyDown={handleKeyDown}
                className={`w-full border-0 outline-none bg-transparent ${
                  isTopLevelFolder ? 'text-lg font-semibold' : 'text-sm'
                }`}
                autoFocus
              />
            ) : (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isCard) handleCardClick();
                    else handleFolderClick();
                  }}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  className={`truncate text-left transition-colors ${
                    isTopLevelFolder
                      ? 'text-lg font-semibold text-gray-900 hover:text-blue-700'
                      : isSubfolder
                      ? 'text-sm font-medium text-gray-800 hover:text-blue-600'
                      : 'text-sm text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {item.name}
                </button>
                {item.is_folder && itemCount > 0 && (
                  <span className="flex-shrink-0 flex items-center text-xs text-gray-400">
                    <Hash className="h-3 w-3 mr-0.5" />{itemCount}
                  </span>
                )}
              </>
            )}
          </div>

          {/* Add buttons - only for folders and instance owners */}
          {item.is_folder && isInstanceOwner && (
            <div className="flex items-center space-x-1">
              {item.parent_id === null && (
                <button
                  onClick={(e) => { e.stopPropagation(); handleAddFolder(); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className="p-1 hover:bg-blue-100 rounded bg-blue-50"
                  title="Add subfolder"
                >
                  <Folder className="h-3 w-3 text-blue-600" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); handleAddCard(); }}
                onMouseDown={(e) => e.stopPropagation()}
                className="p-1 hover:bg-gray-200 rounded bg-gray-100"
                title="Add card"
              >
                <Plus className="h-3 w-3 text-gray-700" />
              </button>
            </div>
          )}

          {/* Zap / Practice button for cards */}
          {!item.is_folder && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowCardPracticePicker(true); }}
              onMouseDown={(e) => e.stopPropagation()}
              title="Practice"
              className="ml-1 p-1.5 rounded bg-amber-50 hover:bg-amber-100 transition-colors"
            >
              <Zap className="h-3.5 w-3.5 text-amber-600" />
            </button>
          )}

          {/* Zap button for folders */}
          {item.is_folder && (isInstanceOwner || flattenTree(item.children ?? []).some(c => !c.is_folder)) && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowFolderPracticePicker(true); }}
              onMouseDown={(e) => e.stopPropagation()}
              title="Actions"
              className="ml-1 p-1.5 rounded bg-amber-50 hover:bg-amber-100 transition-colors"
            >
              <Zap className="h-3.5 w-3.5 text-amber-600" />
            </button>
          )}

          {/* Actions dropdown menu */}
          <div className="ml-2">
            <DropdownMenu
              items={dropdownItems}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>
        </div>

        {/* Mobile layout - responsive stacked design */}
        <div 
          className="lg:hidden"
          onDragOver={(e) => {
            // Allow drop on nested elements
            if (item.is_folder) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
          onDrop={(e) => {
            // Forward drop to parent handler
            if (item.is_folder) {
              handleDrop(e);
            }
          }}
        >
          {/* Top row: Icon, name, and expand button */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center flex-1 min-w-0">
              {/* Selection checkbox - only for cards in selection mode */}
              {isSelectionMode && isCard && (
                <input
                  type="checkbox"
                  checked={isSelected}
                  onChange={(e) => {
                    e.stopPropagation();
                    onSelectCard?.(item.id, e.target.checked);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="mr-2 h-4 w-4 rounded border-gray-300 text-blue-600 cursor-pointer flex-shrink-0"
                />
              )}
              {/* Drag handle - hidden on mobile */}
              {!isMobile && (
                <div
                  className="mr-2 transition-opacity opacity-0 group-hover:opacity-100 cursor-grab active:cursor-grabbing"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <GripVertical className="h-3 w-3 text-gray-400" />
                </div>
              )}

              {/* Expand/collapse button - only for items with children */}
              {hasChildren && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggle(item.id);
                  }}
                  onMouseDown={(e) => e.stopPropagation()}
                  className={`mr-2 p-1.5 hover:bg-gray-200 rounded transition-colors flex-shrink-0 ${
                    isTopLevelFolder ? 'hover:bg-blue-200' : ''
                  }`}
                >
                  {item.isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-gray-600" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-gray-600" />
                  )}
                </button>
              )}

              {/* Spacer for items without expand button */}
              {!hasChildren && <div className="w-6 mr-2" />}

              {/* Icon - hidden for folders on mobile (expand chevron is sufficient) */}
              <div className={`flex-shrink-0 ${item.is_folder ? 'hidden' : 'mr-3'}`}>
                {!(item.is_folder === true || item.type === 'folder') && (
                  <FileText className="h-3.5 w-3.5 text-gray-500" />
                )}
              </div>

              {/* Name with appropriate styling */}
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleRename}
                    onKeyDown={handleKeyDown}
                    className={`w-full border-0 outline-none bg-transparent ${
                      isTopLevelFolder ? 'text-lg font-semibold' : 'text-sm'
                    }`}
                    autoFocus
                  />
                ) : (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (isCard) handleCardClick();
                      else handleFolderClick();
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    className={`truncate text-left w-full transition-colors ${
                      isTopLevelFolder
                        ? 'text-lg font-semibold text-gray-900 hover:text-blue-700'
                        : isSubfolder
                        ? 'text-sm font-medium text-gray-800 hover:text-blue-600'
                        : 'text-sm text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {item.name}
                    {item.is_folder && itemCount > 0 && (
                      <span className="text-xs text-gray-400 font-normal ml-1.5">#{itemCount}</span>
                    )}
                  </button>
                )}
              </div>
            </div>

            {/* Add icon buttons for folders - mobile */}
            {item.is_folder && isInstanceOwner && (
              <div className="flex items-center gap-1 ml-1">
                <button
                  onClick={(e) => { e.stopPropagation(); handleAddCard(); }}
                  onMouseDown={(e) => e.stopPropagation()}
                  title="Add card"
                  className="p-1.5 hover:bg-gray-200 rounded-lg bg-gray-100 transition-colors"
                >
                  <Plus className="h-3.5 w-3.5 text-gray-700" />
                </button>
              </div>
            )}

            {/* Zap button - mobile */}
            {!item.is_folder && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowCardPracticePicker(true); }}
                onMouseDown={(e) => e.stopPropagation()}
                title="Practice"
                className="ml-1 p-1.5 rounded bg-amber-50 hover:bg-amber-100 transition-colors flex-shrink-0"
              >
                <Zap className="h-3.5 w-3.5 text-amber-600" />
              </button>
            )}
            {item.is_folder && (isInstanceOwner || flattenTree(item.children ?? []).some(c => !c.is_folder)) && (
              <button
                onClick={(e) => { e.stopPropagation(); setShowFolderPracticePicker(true); }}
                onMouseDown={(e) => e.stopPropagation()}
                title="Actions"
                className="ml-1 p-1.5 rounded bg-amber-50 hover:bg-amber-100 transition-colors flex-shrink-0"
              >
                <Zap className="h-3.5 w-3.5 text-amber-600" />
              </button>
            )}

            {/* Actions dropdown menu - always visible on mobile */}
            <div className="ml-1 flex-shrink-0">
              <DropdownMenu
                items={dropdownItems}
                className="opacity-100"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Add item input */}
      {showAddInput && (
        <div 
          className="mt-2"
          style={{ marginLeft: `${indentPx + 24}px` }}
        >
          <AddItemInput
            parentId={item.id}
            type={addType}
            onSubmit={handleAddChild}
            onCancel={() => setShowAddInput(false)}
            placeholder={`Add ${addType}...`}
          />
        </div>
      )}

      {/* Children */}
      {item.isExpanded && item.children && item.children.length > 0 && (
        <div className="mt-1">
          {item.children.map((child) => (
            <TreeNode
              key={child.id}
              item={child}
              onToggle={onToggle}
              onRename={onRename}
              onDelete={onDelete}
              onAddChild={onAddChild}
              onMove={onMove}
              onCategoryRefresh={onCategoryRefresh}
              level={level + 1}
              categoryId={categoryId}
              categories={categories}
              isInstanceOwner={isInstanceOwner}
              isSelectionMode={isSelectionMode}
              isSelected={selectedCardIds ? selectedCardIds.has(child.id) : false}
              selectedCardIds={selectedCardIds}
              onSelectCard={onSelectCard}
            />
          ))}
        </div>
      )}

      {/* Card Detail Modal */}
      {showDetail && (
        <CardDetail
          cardId={item.id}
          initialCard={item}
          onClose={handleDetailClose}
          onSave={handleDetailSave}
          isEditMode={showDetailInEditMode && !item.is_folder}
          isInstanceOwner={isInstanceOwner}
          onDelete={onDelete}
          onMove={onMove}
          categories={categories}
          onEdit={() => {
            setShowDetailInEditMode(true);
          }}
        />
      )}

      {/* Update Card AI Modal */}
      {showUpdateAI && (
        <UpdateCardAIWrapper
          item={item}
          onClose={() => setShowUpdateAI(false)}
          onSuccess={handleAIUpdateSuccess}
        />
      )}

      {/* AI Card Form Modal */}
      {showAICardForm && item.is_folder && (
        <AICardForm
          parentId={item.id}
          parentName={item.name}
          onClose={() => setShowAICardForm(false)}
          onSuccess={handleAICardSuccess}
        />
      )}

      {/* Card Detail Modal for newly created AI card */}
      {newCardId !== null && (
        <CardDetail
          cardId={newCardId}
          onClose={handleNewCardDetailClose}
          onSave={handleNewCardDetailSave}
        />
      )}

      {/* Folder Actions Picker */}
      {showFolderPracticePicker && item.is_folder && (
        <PracticePickerModal
          categoryName={item.name}
          onClose={() => setShowFolderPracticePicker(false)}
          onFlashcards={() => setShowFolderWizardSession(true)}
          onContextual={() => setShowFolderContextualPractice(true)}
          onGenerateText={() => setShowFolderGenerateStory(true)}
          hasCards={flattenTree(item.children ?? []).some(c => !c.is_folder)}
          isOwner={isInstanceOwner}
          onAICard={isInstanceOwner ? () => setShowAICardForm(true) : undefined}
          onAIFolder={isInstanceOwner && item.parent_id === null ? () => setShowAIFolderGenerator(true) : undefined}
          onAddText={isInstanceOwner ? () => setShowAddText(true) : undefined}
        />
      )}

      {/* Folder Contextual Practice */}
      {showFolderContextualPractice && item.is_folder && (
        <ContextualPracticeModal
          folderId={item.id}
          categoryName={item.name}
          leafCardCount={flattenTree(item.children ?? []).filter(c => !c.is_folder).length}
          onClose={() => setShowFolderContextualPractice(false)}
        />
      )}

      {/* Folder Generate Story */}
      {showFolderGenerateStory && item.is_folder && (
        <GenerateStoryModal
          folderId={item.id}
          categoryName={item.name}
          onClose={() => setShowFolderGenerateStory(false)}
        />
      )}

      {showAddText && item.is_folder && (
        <AddTextModal
          folderId={item.id}
          onClose={() => setShowAddText(false)}
          onSaved={() => {}}
        />
      )}

      {/* Card Practice Picker */}
      {showCardPracticePicker && !item.is_folder && (
        <CardPracticePickerModal
          cardName={item.name}
          onClose={() => setShowCardPracticePicker(false)}
          onFlashcard={() => setShowPracticeCard(true)}
          onGeneratePhrases={() => setShowGeneratePhrases(true)}
          onGenerateCollocations={() => setShowCollocations(true)}
          onGenerateSynonyms={() => setShowSynonyms(true)}
          onGenerateAntonyms={() => setShowAntonyms(true)}
          onGenerateImage={() => setShowGenerateImage(true)}
          onUpdateCardAI={isInstanceOwner ? () => setShowUpdateAI(true) : undefined}
        />
      )}

      {/* Card Q&A Practice */}
      {showCardContextualPractice && !item.is_folder && (
        <ContextualPracticeModal
          cardId={item.id}
          categoryName={item.name}
          leafCardCount={1}
          onClose={() => setShowCardContextualPractice(false)}
        />
      )}

      {/* Practice Card Modal */}
      {showPracticeCard && !item.is_folder && (
        <PracticeCard
          cardId={item.id}
          cardName={item.name}
          onClose={() => setShowPracticeCard(false)}
          isInstanceOwner={isInstanceOwner}
          onDelete={onDelete}
          onMove={onMove}
          categories={categories}
        />
      )}

      {showGeneratePhrases && !item.is_folder && (
        <GeneratePhrasesModal
          cardId={item.id}
          cardName={item.name}
          onClose={() => setShowGeneratePhrases(false)}
        />
      )}

      {showCollocations && !item.is_folder && (
        <GenerateCollocationsModal
          cardId={item.id}
          cardName={item.name}
          onClose={() => setShowCollocations(false)}
        />
      )}

      {showSynonyms && !item.is_folder && (
        <GenerateSynonymsModal
          cardId={item.id}
          cardName={item.name}
          onClose={() => setShowSynonyms(false)}
        />
      )}

      {showAntonyms && !item.is_folder && (
        <GenerateAntonymsModal
          cardId={item.id}
          cardName={item.name}
          onClose={() => setShowAntonyms(false)}
        />
      )}

      {showGenerateImage && !item.is_folder && (
        <GenerateImageModal
          cardId={item.id}
          cardName={item.name}
          onClose={() => setShowGenerateImage(false)}
          onSaved={() => setShowGenerateImage(false)}
        />
      )}

      {/* Folder View Modal */}
      {showFolderView && item.is_folder && (
        <FolderView
          folder={item}
          onClose={() => setShowFolderView(false)}
          onRename={onRename}
          onDelete={onDelete}
          onMove={onMove}
          categories={categories}
        />
      )}

      {/* Move To Modal */}
      {showMoveToModal && categories.length > 0 && (
        <MoveToModal
          item={item}
          categories={categories}
          onClose={() => setShowMoveToModal(false)}
          onMove={onMove}
        />
      )}

      {/* Folder Practice Session */}
      {showPracticeSession && item.is_folder && (
        <FolderPracticeSession
          folderId={item.id}
          folderName={item.name}
          categoryId={item.category_id}
          categoryName={categories.find(cat => cat.id === item.category_id)?.name}
          onClose={() => setShowPracticeSession(false)}
        />
      )}

      {/* Folder Wizard Practice Session */}
      {showFolderWizardSession && item.is_folder && (
        <CategoryPracticeSession
          name={item.name}
          initialLeafCards={flattenTree(item.children ?? [])
            .filter((c) => !c.is_folder)
            .map((c) => ({ id: c.id, name: c.name }))}
          onClose={() => setShowFolderWizardSession(false)}
          isInstanceOwner={isInstanceOwner}
          onDelete={isInstanceOwner ? (id) => onDelete(id) : undefined}
          onMove={isInstanceOwner ? onMove : undefined}
          categories={categories}
        />
      )}

      {/* Practice Phrases Modal */}
      {showFolderPracticePhrases && item.is_folder && (
        <FolderPracticePhrasesSession
          folderId={item.id}
          folderName={item.name}
          onClose={() => setShowFolderPracticePhrases(false)}
        />
      )}
      {showPracticePhrases && !item.is_folder && (
        <PracticePhrases
          cardId={item.id}
          cardName={item.name}
          onClose={() => setShowPracticePhrases(false)}
        />
      )}

      {/* Assign Folder Modal */}
      {showAssignModal && item.is_folder && selectedInstanceId && user && (
        <AssignFolderModal
          target={item}
          targetType="folder"
          instanceId={selectedInstanceId}
          currentUserId={user.id}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {}}
        />
      )}

      {showAIFolderGenerator && item.is_folder && categoryId != null && (
        <AIFolderGenerator
          categoryId={categoryId}
          parentId={item.id}
          userEmail={user?.email}
          onClose={() => setShowAIFolderGenerator(false)}
          onSuccess={(newFolderId) => {
            if (onCategoryRefresh && categoryId != null) {
              onCategoryRefresh(categoryId, newFolderId);
            }
          }}
        />
      )}
    </div>
  );
};

// Wrapper component to handle async card conversion
const UpdateCardAIWrapper: React.FC<{
  item: TreeNodeProps['item'];
  onClose: () => void;
  onSuccess: (card: Card) => void;
}> = ({ item, onClose, onSuccess }) => {
  const [card, setCard] = useState<Card | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  React.useEffect(() => {
    const loadCard = async () => {
      try {
        const cardData = await apiClient.getCard(item.id);
        setCard(cardData);
      } catch (error) {
        // If fetch fails, create a minimal card object from the item
        const minimalCard: Card = {
          id: item.id,
          name: item.name,
          parent_id: item.parent_id ?? null,
          is_folder: item.is_folder,
          category_id: item.category_id ?? null,
          example_phrases: item.example_phrases ?? null,
          meanings: item.meanings ?? null,
          grammar_roles: item.grammar_roles ?? null,
          collocations: item.collocations ?? null,
          synonyms: item.synonyms ?? null,
          antonyms: item.antonyms ?? null,
          related_words: null,
          word_forms: null,
          videos: null,
          use_count: item.use_count ?? 0,
          notes: item.notes ?? null,
          created_at: item.created_at ?? new Date().toISOString(),
          user_created: 0,
          children: null,
        };
        setCard(minimalCard);
      } finally {
        setIsLoading(false);
      }
    };

    loadCard();
  }, [item]);

  if (isLoading || !card) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="flex items-center justify-center">
            <div className="text-gray-600">Loading card...</div>
          </div>
        </div>
      </div>
    );
  }

  return <UpdateCardAI card={card} onClose={onClose} onSuccess={onSuccess} />;
};